import {PersonIcon} from '@primer/octicons-react'
import {Link, Text, Octicon} from '@primer/react'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {LABELS} from '../constants'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {AssignedEvent$key} from './__generated__/AssignedEvent.graphql'
import {userHovercardPath} from '@github-ui/paths'
import {TimelineRow} from './row/TimelineRow'

type AssignedEventProps = {
  queryRef: AssignedEvent$key
  issueUrl: string
  highlightedEventId?: string
  refAttribute?: React.MutableRefObject<HTMLDivElement | null>
}

export function AssignedEvent({queryRef, issueUrl, highlightedEventId, refAttribute}: AssignedEventProps): JSX.Element {
  const {actor, createdAt, assignee, databaseId} = useFragment(
    graphql`
      fragment AssignedEvent on AssignedEvent {
        databaseId
        createdAt
        actor {
          ...EventActor
          ...EventActorAvatarItemBadge
          login
        }
        assignee {
          ... on User {
            avatarUrl
            login
          }
          ... on Mannequin {
            avatarUrl
            login
          }
          ... on Organization {
            avatarUrl
            login
          }
          ... on Bot {
            avatarUrl
            login
          }
        }
      }
    `,
    queryRef,
  )
  const assigneeNode: JSX.Element =
    actor?.login === assignee?.login ? (
      <Text>{LABELS.timeline.selfAssignedThis} </Text>
    ) : (
      <>
        {`${LABELS.timeline.assigned} `}
        <Octicon icon={PersonIcon} />{' '}
        {assignee?.login && (
          <Link
            data-hovercard-url={userHovercardPath({owner: assignee.login})}
            href={`/${assignee?.login}`}
            sx={{color: 'fg.default', mr: 1}}
            muted
          >
            {assignee.login}
          </Link>
        )}
      </>
    )

  const highlighted = String(databaseId) === highlightedEventId

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      actorAvatar={actor}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
    >
      {assigneeNode}
    </TimelineRow>
  )
}

try{ AssignedEvent.displayName ||= 'AssignedEvent' } catch {}